<template>
  <main class="cover-auth">
    <div class="widget-auth">
      <a href="#">
        <div class="text-center mb-8">
          <img src="/public/images/logo-wide.svg" class="auth-logo" />
        </div>
      </a>

      <h2 class="font-semibold text-center my-6">Activation de votre compte</h2>

      <!-- Chargement des informations -->
      <div v-if="loadingInfo" class="text-center pb-5">
        <p class="text-read text-grey-600">Chargement de vos informations...</p>
      </div>

      <!-- Message d'erreur -->
      <div v-if="errorMessage" class="alert alert-danger mb-5">
        <p class="text-sm text-center">{{ errorMessage }}</p>
      </div>

      <!-- Affichage des informations du profil -->
      <div v-if="!loadingInfo && userInfo" class="profile-info mb-5">
        <p class="text-read text-grey-600 text-center pb-4">
          Bienvenue sur Coop Finance ! Vérifiez vos informations ci-dessous.
        </p>

        <div class="info-card">
          <div class="info-row">
            <span class="info-label">Nom complet:</span>
            <span class="info-value">{{ userInfo.prenom }} {{ userInfo.nom }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Email:</span>
            <span class="info-value">{{ userInfo.email }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Téléphone:</span>
            <span class="info-value">{{ userInfo.telephone }}</span>
          </div>
          <div class="info-row" v-if="userInfo.dateNaissance">
            <span class="info-label">Date de naissance:</span>
            <span class="info-value">{{ formatDate(userInfo.dateNaissance) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Collectivité:</span>
            <span class="info-value">{{ userInfo.collectiviteNom }}</span>
          </div>
          <div class="info-row" v-if="userInfo.fonction">
            <span class="info-label">Fonction:</span>
            <span class="info-value">{{ userInfo.fonction }}</span>
          </div>
          <div class="info-row" v-if="userInfo.dateEngagement">
            <span class="info-label">Date d'engagement:</span>
            <span class="info-value">{{ formatDate(userInfo.dateEngagement) }}</span>
          </div>
          <div class="info-row" v-if="userInfo.salaire">
            <span class="info-label">Salaire brut:</span>
            <span class="info-value">{{ formatMontant(userInfo.salaire) }} F CFA</span>
          </div>
          <div class="info-row" v-if="userInfo.quotiteCessiblePourcent">
            <span class="info-label">Quotité cessible:</span>
            <span class="info-value">{{ userInfo.quotiteCessiblePourcent }}% ({{ formatMontant(userInfo.quotiteCessibleMontant) }} F CFA)</span>
          </div>
        </div>

        <form class="form" @submit.prevent="handleConfirm">
          <!-- Checkbox de confirmation -->
          <div class="form-check my-5">
            <input
              type="checkbox"
              id="confirmIdentity"
              v-model="identityConfirmed"
              class="form-check-input"
            >
            <label for="confirmIdentity" class="form-check-label">
              Je confirme que ces informations correspondent à mon identité
            </label>
          </div>

          <button
            type="submit"
            class="btn lg w-100 flex-center mt-5 mb-7"
            :disabled="loading || !identityConfirmed"
          >
            <img src="/public/icons/icon-valider.svg" class="btn-icon" alt="" />
            <span>{{ loading ? 'Confirmation en cours...' : 'Confirmer et continuer' }}</span>
          </button>

          <router-link to="/auth/sign-in">
            <h5 class="text-primary-color text-center">Vous avez déjà un compte ?</h5>
          </router-link>
        </form>
      </div>
    </div>
  </main>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { API_CONFIG, TokenStorage } from '@/services/apiConfig'

interface UserInfo {
  id: number
  nom: string
  prenom: string
  telephone: string
  email: string
  fonction?: string
  salaire?: number
  dateNaissance?: string
  dateEngagement?: string
  collectiviteNom: string
  collectiviteId: number
  paysNom: string
  quotiteCessiblePourcent?: number
  quotiteCessibleMontant?: number
  statut: string
}

export default defineComponent({
  name: 'InvitationView',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const invitationCode = ref('')
    const userInfo = ref<UserInfo | null>(null)
    const loadingInfo = ref(true)
    const loading = ref(false)
    const errorMessage = ref('')
    const identityConfirmed = ref(false)

    const fetchUserInfo = async () => {
      loadingInfo.value = true
      errorMessage.value = ''

      try {
        console.log('[Invitation] Récupération des informations utilisateur...')

        const response = await axios.get(
          `${API_CONFIG.baseURL}/invitation/${invitationCode.value}/info`,
          {
            headers: API_CONFIG.headers
          }
        )

        console.log('[Invitation] Informations reçues:', response.data)

        if (response.data.status === 0 && response.data.data) {
          userInfo.value = response.data.data
        } else {
          errorMessage.value = response.data.message || 'Code d\'invitation invalide'
        }
      } catch (error: any) {
        console.error('[Invitation] ❌ Erreur:', error)
        errorMessage.value = error.response?.data?.message || 'Une erreur est survenue lors de la récupération de vos informations'
      } finally {
        loadingInfo.value = false
      }
    }

    onMounted(() => {
      // Récupérer le code d'invitation depuis l'URL
      invitationCode.value = route.params.code as string
      console.log('[Invitation] Code reçu:', invitationCode.value)

      // Charger les informations de l'utilisateur
      fetchUserInfo()
    })

    const formatDate = (dateString: string | undefined): string => {
      if (!dateString) return 'Non renseignée'

      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      })
    }

    const formatMontant = (montant: number | undefined): string => {
      if (!montant) return '0'

      return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(montant)
    }

    const handleConfirm = async () => {
      if (!identityConfirmed.value) {
        errorMessage.value = 'Veuillez confirmer votre identité pour continuer'
        return
      }

      loading.value = true
      errorMessage.value = ''

      try {
        console.log('[Invitation] Confirmation de l\'identité...')

        const response = await axios.post(
          `${API_CONFIG.baseURL}/invitation/${invitationCode.value}/confirm`,
          {},
          {
            headers: API_CONFIG.headers
          }
        )

        console.log('[Invitation] Réponse reçue:', response.data)

        if (response.data.status === 0) {
          console.log('[Invitation] ✅ Identité confirmée, redirection vers création du code de sécurité...')

          // Stocker le token et les données utilisateur pour authentification
          if (response.data.token) {
            TokenStorage.setToken(response.data.token)
            TokenStorage.setUser(response.data.data)
            console.log('[Invitation] Token d\'authentification stocké')
          }

          // Stocker les données pour les prochaines étapes
          sessionStorage.setItem('invitation_user_id', response.data.data.userId.toString())
          sessionStorage.setItem('invitation_user_salaire', response.data.data.salaire?.toString() || '0')
          sessionStorage.setItem('invitation_user_nom', response.data.data.nom)
          sessionStorage.setItem('invitation_user_prenom', response.data.data.prenom)
          sessionStorage.setItem('invitation_user_email', response.data.data.email)
          sessionStorage.setItem('invitation_user_telephone', response.data.data.telephone)
          sessionStorage.setItem('invitation_user_collectivite', response.data.data.collectivite || 'Coop Finance')

          // Rediriger vers la page de création du code de sécurité
          router.push('/auth/sign-up-activation')
        } else {
          errorMessage.value = response.data.message || 'Erreur lors de la confirmation'
        }
      } catch (error: any) {
        console.error('[Invitation] ❌ Erreur:', error)
        errorMessage.value = error.response?.data?.message || 'Une erreur est survenue lors de la confirmation'
      } finally {
        loading.value = false
      }
    }

    return {
      userInfo,
      loadingInfo,
      loading,
      errorMessage,
      identityConfirmed,
      formatDate,
      formatMontant,
      handleConfirm
    }
  }
})
</script>

<style scoped>
.auth-logo {
  max-width: 200px;
  height: auto;
}

.rounded-50 {
  border-radius: 50px;
}

.text-center {
  text-align: center;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.alert {
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.alert-danger {
  background-color: #fee;
  border: 1px solid #fcc;
  color: #c33;
}

.info-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e5e7eb;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: 600;
  color: #6b7280;
  font-size: 0.9rem;
}

.info-value {
  font-weight: 500;
  color: #111827;
  text-align: right;
  flex: 1;
  margin-left: 1rem;
}

.form-check {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
}

.form-check-input {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #0ea5a4;
}

.form-check-label {
  cursor: pointer;
  font-size: 0.95rem;
  color: #374151;
  user-select: none;
}
</style>
